export const formatDate = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const IST_OFFSET_MS = (5 * 60 + 30) * 60 * 1000;

// Jugaad: backend sends created_at with a "+05:30" suffix that's already wrong
// (off by 5:30) — the raw digits are actually UTC. Strip whatever offset is
// there, treat the raw digits as UTC, then add 5:30 on top to land on the
// right IST wall-clock time.
export const formatIST = (value: string): string => {
  if (!value) return '—';
  const naive = value.replace(/([+-]\d{2}:?\d{2}|Z)$/, '');
  const d = new Date(new Date(`${naive}Z`).getTime() + IST_OFFSET_MS);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

// For values that are already naive IST wall-clock (e.g. notified_at, set via
// datetime.now(ZoneInfo("Asia/Kolkata")) on the backend) — format as-is, with
// no offset shift. Using formatIST on these would double-shift by +5:30.
export const formatISTWallClock = (value: string): string => {
  if (!value) return '—';
  const naive = value.replace(/([+-]\d{2}:?\d{2}|Z)$/, '');
  const d = new Date(`${naive}Z`);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

// 24-hour, e.g. "02 May 2026, 09:30" — used for the consent ledger timeline.
export const formatTimelineDate = (value: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// Backend sends due_in as a phrase, e.g. "89 days remaining", "Overdue by 3 days",
// "Today" — pull out the day count so it reads consistently: "89 Days".
export const formatDueIn = (dueIn: string): string => {
  if (!dueIn) return '';
  const match = String(dueIn).match(/-?\d+/);
  if (!match) return dueIn;
  return `${match[0]} Day${match[0] === '1' ? '' : 's'}`;
};

const ISO_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

// Formats a value only if it looks like a full ISO timestamp (has a time component),
// leaving bare dates (e.g. a date of birth) and non-date values untouched.
export const formatIfTimestamp = (value: unknown): unknown =>
  typeof value === 'string' && ISO_DATETIME_RE.test(value) ? formatDate(value) : value;
